/* Generated by restful-react */

import React from 'react';

import {
	Get,
	GetProps,
	useGet,
	UseGetProps,
	Mutate,
	MutateProps,
	useMutate,
	UseMutateProps,
} from 'restful-react';

export const SPEC_VERSION = '1.0.33';
export interface ActionsUpdateRequest {
	data?: ActionsUpdateData;
}

export interface ActionsUpdateData {
	member_actions?: string;
	account_actions?: string;
}

export interface Transaction {
	data?: {
		transactions?: AccountTransaction[];
	};
	meta?: Meta;
}

/**
 * Details of a transaction
 */
export interface AccountTransaction {
	/**
	 * Identifier for a transaction.
	 */
	id: number;
	/**
	 * transaction type title.
	 */
	title?: string;
	transaction_group: TransactionGroup;
	/**
	 * type of the transaction made;
	 */
	type: string;
	/**
	 * Date when the contribution was paid. Format in yyyy-MM-dd
	 */
	date_paid: string;
	/**
	 * Represents the status of either the contribution or the transaction. As both (transaction & contribution) can carry the same value, status is grouped.
	 *
	 */
	status: string;
	/**
	 * The account number that is provided to super members that is included in all communication with the member.
	 */
	account_number: string;
	/**
	 * Sum total of all contributions made by the employer
	 */
	total: number;
	apportion?: Apportions;
	tax_detail?: TaxDetail;
	benefit_detail?: BenefitDetail;
	pension_detail?: PensionDetail;
	reportable_contribution_detail?: ReportableContributionDetail;
	transaction_detail?: TransactionDetail;
}

export type TransactionDetail =
	| TransactionTransferIn
	| TransactionClaim
	| TransactionContribution
	| TransactionJournal;

export interface TransactionGroup {
	/**
	 * transaction group code
	 */
	code: string;
	/**
	 * transaction group description
	 */
	description: string;
}

export type Apportions = Apportion[];

export interface Apportion {
	source?:
		| 'Employer Additional'
		| 'Employer'
		| 'Salary Sacrifice'
		| 'Account'
		| 'Spouse'
		| 'Spouse Transfer'
		| 'Direct Investment'
		| 'Insurance Credit'
		| 'Rollover'
		| 'Pension'
		| 'Member';
	amount?: number;
	investments?: Investment[];
}

export interface Investment {
	/**
	 * Id of the investment
	 */
	investment_id: string;
	/**
	 * description of investment
	 */
	investment_description?: string;
	amount: number;
	units?: number;
}

export type BenefitDetail = ContributionComponent[];

export type PensionDetail = ContributionComponent[];

export type TaxDetail = ContributionComponent[];

export type ReportableContributionDetail = ContributionComponent[];

export interface ContributionComponent {
	code: string;
	amount: number;
	description?: string;
}

/**
 * descriptor to identify the transaction
 */
export type TransactionCode =
	| 'transaction_transfer_in'
	| 'transaction_claim'
	| 'transaction_contribution'
	| 'transaction_journal';

/**
 * Represents Transfer In transaction detail
 */
export interface TransactionTransferIn {
	transaction_code?: TransactionCode;
	type_code: {};
	type_description?: TypeDescription;
	reason_code?: ReasonCode;
	reason_description?: ReasonDescription;
	employer_number?: EmployerNumber;
	registered_name?: RegisteredName;
	trading_name?: string;
	payer_abn?: Abn;
	/**
	 * SFN of the payer
	 */
	payer_sfn?: string;
	/**
	 * SPIN of the payer
	 */
	payer_spin?: string;
	/**
	 * Fund name of the payer.
	 */
	fund_name?: string;
	from_account_number?: AccountNumber;
}

/**
 * Represents Claim transaction detail
 */
export interface TransactionClaim {
	transaction_code?: TransactionCode;
	type_code: TypeCode;
	type_description?: TypeDescription;
	reason_code: ReasonCode;
	reason_description?: ReasonDescription;
	category: string;
	category_description?: string;
	/**
	 * date the transaction was received.
	 */
	received_date?: string;
	/**
	 * date when transaction was calculated.
	 */
	calculation_date?: string;
	claim_distribution: ClaimDistribution;
}

/**
 * Represents Contribution transaction detail
 */
export interface TransactionContribution {
	transaction_code?: TransactionCode;
	employer_number: EmployerNumber;
	pay_period_from_date: PayPeriodFromDate;
	pay_period_to_date: PayPeriodToDate;
	/**
	 * number of pays
	 */
	pay_count?: number;
	registered_name: RegisteredName;
	trading_name?: string;
	contribution_status: ContributionStatus;
	adjusted_reason?: string;
}

/**
 * Represents journal transaction detail
 */
export interface TransactionJournal {
	transaction_code?: TransactionCode;
	type_code: TypeCode;
	type_description?: TypeDescription;
}

/**
 * type code of the transaction
 */
export type TypeCode = string;

/**
 * description of the transaction type code
 */
export type TypeDescription = string;

/**
 * reason code for the transaction
 */
export type ReasonCode = string;

/**
 * description of the reason code
 */
export type ReasonDescription = string;

/**
 * A number to Uniquely identify an employer
 */
export type EmployerNumber = string;

/**
 * Business registered name ?registeredName
 */
export type RegisteredName = string;

/**
 * status of the contribution
 */
export type ContributionStatus =
	| 'Pending'
	| 'Awaiting Payment'
	| 'Processed'
	| 'Reversed';

/**
 * The date from which the pay period starts. ISO8601 format.
 */
export type PayPeriodFromDate = string;

/**
 * The date on which the pay period ends. ISO8601 format.
 */
export type PayPeriodToDate = string;

/**
 * Australian Business Number.
 */
export type Abn = string;

/**
 * distribution details of the claim
 */
export interface ClaimDistribution {
	gross_available?: number;
	gross_claim?: number;
	accumulation?: number;
	notional?: number;
	anti_detriment?: number;
	insurance?: number;
	claim_tax?: number;
	net_claim?: number;
	payment_details?: PaymentDetails;
}

/**
 * payment details of the claim
 */
export interface PaymentDetails {
	payment_method?: PaymentMethod;
	payment_method_code?: number;
	payment_method_description?: string;
	payee_name?: string;
	receiving_fund_name?: string;
	anti_detriment_disbursement?: number;
	gross_disbursement?: number;
	net_disbursement?: number;
	tax_calculated?: number;
	tax_overridden?: number;
	payee_address?: PayeeAddress;
}

export type PaymentMethod =
	| 'Pay To ATO'
	| 'Pay To ATO Paper'
	| 'Pay to Financial'
	| 'Family Law Spouse'
	| 'Pay to Institution'
	| 'Pay to Member'
	| 'Pay to NonFinancial'
	| 'Pay Pension'
	| 'Pay to Thirdparty';

/**
 * payee address of the claim
 */
export interface PayeeAddress {
	address_line_1?: string;
	address_line_2?: string;
	suburb?: string;
	state?: string;
	country_code?: string;
	country?: string;
	dpid?: number;
}

/**
 * The account number that is provided to super members that is included in all communication with the member
 */
export type AccountNumber = string;

export interface ExpSpouseContributionData {
	data?: ExpSpouseContributionDataType;
}

/**
 * Example properties for a spouse contribution communication
 */
export interface ExpSpouseContributionDataType {
	first_name: string;
	email_address?: string;
	mobile_number?: string;
	/**
	 * Accumulation or Pension
	 */
	product_name: string;
	account_number: string;
}

export interface ExpNotifyEmployerData {
	data?: ExpNotifyEmployerDataType;
}

/**
 * Example properties for a spouse contribution communication
 */
export interface ExpNotifyEmployerDataType {
	email_address: string;
	/**
	 * Accumulation or Pension
	 */
	product_name: string;
	account_number: string;
}

export interface HomeWorkNumber {
	type: 'WORK' | 'HOME';
	number: string;
}

export type HomeWorkNumbers = HomeWorkNumber[];

export interface MemberHomeWorkUpdate {
	phone_numbers?: HomeWorkNumbers;
}

export interface MemberHomeWorkData {
	data?: MemberHomeWorkUpdate;
}

export interface MemberMobileData {
	data?: MemberMobileUpdate;
}

export interface MemberMobileUpdate {
	auth_code?: string;
	mobile_number: string;
	sso_token?: string;
	verification_details?: {
		application_number?: string;
		verification_id?: string;
	};
}

export interface UpdateMobileData {
	/**
	 * RFC 3339 compliant date time 2017-07-21T17:32:28+10:00
	 */
	restriction_end_date_time?: string;
	restricted_applications?: string[];
}

/**
 * Successful phone number update (includes restriction date for mobile type)
 */
export interface UpdateMobileResponse {
	data?: UpdateMobileData;
}

export interface FipEligibiltyResponse {
	data?: FipEligibiltyResponseData;
}

/**
 * Example properties for FipEligibilty
 */
export interface FipEligibiltyResponseData {
	topics?: FipEligibiltyResponseDataType[];
}

/**
 * properties for FipEligibiltyData
 */
export interface FipEligibiltyResponseDataType {
	topic_code?: string;
	/**
	 * sample values ["hidden","true","false"]
	 */
	status?: string;
	reason_code?: string[];
}

/**
 * Successful retrieval of set-up profile actions
 */
export interface ExpMemberProfileActionsResponse {
	data?: ExpMemberProfileActionsData;
}

export interface ExpMemberProfileActionsData {
	has_pending_profile_action?: boolean;
	profile_actions?: ExpMemberProfileActions;
}

export interface ExpMemberProfileActions {
	review_your_personal_details?: boolean;
	add_your_mobile_number?: boolean;
	manage_your_beneficiaries?: boolean;
	communication_preference?: boolean;
	verified_email_address?: boolean;
	verified_mobile_number?: boolean;
}

export interface ExpMemberData {
	data?: ExpMember;
}

export interface ExpMember {
	party_type_id: string;
	customer_reference_number: string;
	/**
	 * Title of the member contact
	 */
	salutation: string;
	/**
	 * First name of the member contact
	 */
	first_name: string;
	/**
	 * Last name of the member contact
	 */
	last_name: string;
	/**
	 * Birth date of the member contact
	 */
	birth_date: string;
	/**
	 * age of the member contact
	 */
	age: number;
	/**
	 * gender of the member contact
	 */
	gender: string;
	/**
	 * email address of the member contact
	 */
	email_address: string;
	/**
	 * status of the member contact (Active, Inactive)
	 */
	member_status: string;
	/**
	 * indicator if first login in web portal
	 */
	first_web_portal_login?: boolean;
	/**
	 * indicator of stop payment
	 */
	stop_payment_indicator?: string;
	/**
	 * stop payment code
	 */
	stop_payment_code?: string;
	/**
	 * classification id of the member
	 */
	classification_id: string;
	/**
	 * classification description transformed as insurance category
	 */
	classification_description?: string;
	/**
	 * division id of the member
	 */
	division_id?: string;
	/**
	 * occupation of the member
	 */
	occupation_description?: string;
	/**
	 * tfn status of the member
	 */
	tfn_status?: string;
	/**
	 * super match consent code
	 */
	super_match_consent_code?: number;
	/**
	 * Eligible service start date of the member
	 */
	eligible_service_start_date?: string;
	/**
	 * phone numbers of the member contact
	 */
	phone_numbers: ExpPhoneNumber[];
	/**
	 * addresses of the member contact
	 */
	addresses: ExpAddress[];
	/**
	 * accounts of the member contact
	 */
	accounts: ExpMemberAccount[];
	profile_actions?: ExpProfileActionsResponse;
}

export interface ExpProfileActionsResponse {
	review_your_personal_details?: boolean;
	add_your_mobile_number?: boolean;
	manage_your_beneficiaries?: boolean;
	communication_preference?: boolean;
	tell_your_employer_last_updated_date?: string;
	consolidate_your_super_last_updated_date?: string;
	make_contribution_last_updated_date?: string;
	verified_mobile_number?: boolean;
	verified_email_address?: boolean;
}

export interface ExpAddress {
	type: 'POSTAL' | 'RESIDENTIAL';
	address_line1: string;
	address_line2?: string;
	postcode?: string;
	city?: string;
	state?: string;
	country: string;
	status: 'active' | 'inactive';
}

export interface ExpMemberAccount {
	account_number?: string;
	/**
	 * Accumulation or Pension
	 */
	product_name?: string;
	/**
	 * Status of account (Active, Inactive, In-Active, Provisional, Holding, Pending)
	 */
	account_status?: string;
	/**
	 * Date when the member joined
	 */
	joined_date?: string;
	/**
	 * Account Classification
	 */
	product_classification?: string;
	/**
	 * Application type (Direct, Deemed)
	 */
	application_type?: string;
}

export interface ExpPhoneNumber {
	type: 'WORK' | 'HOME' | 'MOBILE';
	number: string;
}

export type SourceTypes = 'member' | 'member-mobile';

export type ProductTypes = 'Accumulation' | 'Pension';

export type TransactionTypes =
	| 'ALL'
	| 'ADJUSTMENTS'
	| 'CONTRIBUTIONS'
	| 'FEES'
	| 'GOVERNMENT_TAXES'
	| 'INSURANCE_PREMIUMS'
	| 'INVESTMENTS_RETURNS'
	| 'OTHER'
	| 'TRANSFERS'
	| 'WITHDRAWALS'
	| 'INCOME_PAYMENTS';

export type SummaryTypes =
	| 'ACCOUNT_BALANCES_ESTIMATED_EARNINGS'
	| 'INVESTMENT_RETURNS'
	| 'CONTRIBUTIONS'
	| 'INCOME_PAYMENTS'
	| 'FEES'
	| 'GOVERNMENT_TAXES'
	| 'INSURANCE_PREMIUMS';

/**
 * Source of contributions.
 */
export type BpaySourceContributionValues = 'MEMBER' | 'SPOUSE';

export interface Error {
	applicationErrorCode?: string;
	errorDescription?: string;
}

/**
 * Maximum items to return.
 */
export type ItemLimit = number;

/**
 * Item result offset. Used in conjunction with limit.
 */
export type ItemOffset = number;

/**
 * Service status found
 */
export interface ServiceStatusResult {
	data?: ServiceStatusData;
}

export interface ServiceStatusData {
	service_status?: ServiceStatusDataList[];
}

export interface ServiceStatusDataList {
	key?: string;
	value?: string;
}

export interface ServiceStatusUpdate {
	data?: ServiceStatusUpdateData;
}

export interface ServiceStatusUpdateData {
	key?: string;
	value?: string;
}

export interface TalAccountLookup {
	data?: TalAccountLookupData;
}

/**
 * Example properties for TAL Account Lookup Data
 */
export interface TalAccountLookupData {
	/**
	 * Account Number
	 */
	account_number: string;
	/**
	 * Accumulation or Pension
	 */
	product_name: string;
	/**
	 * MAKE_A_CLAIM or MANAGE_INSURANCE
	 */
	action: 'MAKE_A_CLAIM' | 'MANAGE_INSURANCE';
}

export interface TalRedirectUri {
	data?: TalRedirectUriData;
}

export interface TalRedirectUriData {
	/**
	 * Redirect URI with GUID
	 */
	redirect_uri: string;
}

export interface AccountSnapshot {
	data: AccountSnapshotResponseData;
}

export interface AccountSnapshotResponseData {
	product_name?: ProductTypes;
	account_number?: string;
	account_snapshot?: AccountSnapshotResponse;
}

export interface AccountSnapshotResponse {
	account_balances_estimated_earnings?: number;
	account_balances_current?: number;
	/**
	 * Effective/as-of date and time of the balance data when Account Snapshot is returned in failover mode.
	 */
	failover_balance_asof_date?: string;
	contributions?: number;
	income_payments?: number;
	balance_history?: BalanceHistory[];
}

export interface BalanceHistory {
	financial_year_end_date?: string;
	balance?: number;
	/**
	 * Offset from the current financial year. 0 means current FY, 1 means previous FY.
	 */
	current_fy_offset?: number;
}

/**
 * Response wrapper for Dashboard Investments component data.
 */
export interface InvestmentsResponse {
	data: InvestmentsResponseData;
}

/**
 * Dashboard Investments summary for the selected account.
 */
export interface InvestmentsResponseData {
	product_name: ProductTypes;
	account_number: string;
	effective_date: string;
	total_amount: number;
	has_pending_switch?: boolean;
	investment_allocation: InvestmentAllocation;
	account: InvestmentAccountFlags;
}

/**
 * Investment allocation summary for the selected account.
 */
export interface InvestmentAllocation {
	investment_choices?: InvestmentChoice[];
	investment_options: InvestmentOption[];
	total_percentage?: number;
}

/**
 * Selected investment choice details.
 */
export interface InvestmentChoice {
	investment_id: string;
	investment_description: string;
	investment_category: string;
	proportion_value: number;
	hierarchy?: number;
	display_order: number;
	as_at?: string;
	one_year_rates?: InvestmentPerformanceYearRates;
	three_year_rates?: InvestmentPerformanceYearRates;
	five_year_rates?: InvestmentPerformanceYearRates;
	ten_year_rates?: InvestmentPerformanceYearRates;
	status?: 'ACTIVE' | 'INACTIVE';
	is_editable?: boolean;
	maximum_allocation?: number;
	is_selectable?: boolean;
}

/**
 * Current investment option details.
 */
export interface InvestmentOption {
	investment_id: string;
	investment_description: string;
	investment_category: string;
	amount: number;
	units: number;
	current_allocation: number;
	hierarchy?: number;
	display_order: number;
	as_at?: string;
	one_year_rates?: InvestmentPerformanceYearRates;
	three_year_rates?: InvestmentPerformanceYearRates;
	five_year_rates?: InvestmentPerformanceYearRates;
	ten_year_rates?: InvestmentPerformanceYearRates;
	status?: 'ACTIVE' | 'INACTIVE';
	is_editable?: boolean;
	maximum_allocation?: number;
	is_selectable?: boolean;
}

/**
 * Investment performance rates for a reporting period.
 */
export interface InvestmentPerformanceYearRates {
	australiansuper: number;
	benchmark: number;
	cpi: number;
}

/**
 * Account flags for the Dashboard Investments component.
 */
export interface InvestmentAccountFlags {
	is_md_eligible?: boolean;
	is_ttr?: boolean;
	is_pension?: boolean;
}

export interface TrustedDevice {
	device_id?: string;
	last_login?: string;
	device_profile?: string;
}

export interface PreviousLoginDevice {
	device_id?: string;
	last_login?: string;
	device_profile?: string;
}

export interface TrustedDevicesResponse {
	data?: {
		trusted_devices?: TrustedDevice[];
		previous_login?: PreviousLoginDevice;
	};
}

export interface DeleteTrustedDeviceRequest {
	data?: DeleteTrustedDeviceRequestData;
}

export interface DeleteTrustedDeviceRequestData {
	/**
	 * Auth token from OTP validation
	 */
	auth_code: string;
	/**
	 * List of device ids
	 */
	device_id: string[];
}

export interface GenerateOtpRequest {
	data?: GenerateOtpRequestData;
}

export interface GenerateOtpRequestData {
	/**
	 * The context of this OTP
	 */
	context: string;
}

export interface GenerateOtpResponse {
	data?: GenerateOtpResponseData;
}

export interface GenerateOtpResponseData {
	/**
	 * Unique id for this identity
	 */
	party_type_id?: string;
	/**
	 * Count of attemps left for this OTP Process
	 */
	attempts_left?: number;
	/**
	 * A token issued for OTP process
	 */
	auth_code?: string;
}

export interface ValidateOtpRequest {
	data?: ValidateOtpRequestData;
}

export interface ValidateOtpRequestData {
	/**
	 * The OTP code that customer receives
	 */
	code: string;
	/**
	 * The token issued for this OTP process
	 */
	auth_code: string;
}

export interface ValidateOtpResponse {
	data?: ValidateOtpResponseData;
}

export interface ValidateOtpResponseData {
	/**
	 * In some context, a new otp auth token is issued for search needed
	 */
	auth_code?: string;
}

export interface RolloverFundRequest {
	data: RolloverFundDataRequest;
}

export interface RolloverFundDataRequest {
	email_address: string;
	rollover_funds: RolloverFundsDetailsRequest[];
}

export interface RolloverFundsDetailsRequest {
	is_smsf: boolean;
	source_fund_code: string;
	source_account_number: string;
	source_messaging_provider_id: string;
	rollover_request_date: string;
	transfer_whole_balance: boolean;
	transfer_amount: number;
	rollover_delivery_source: 'Online' | 'Join';
}

export interface RolloverFundResponse {
	data: RolloverFundResponseData;
}

export interface RolloverFundResponseData {
	rollover_funds: RolloverFundsDetailsResponse[];
}

export interface RolloverFundsDetailsResponse {
	abn: string;
	fund_name: string;
	usi: string;
	esa_alias: string;
	source_account_number: string;
	received_date: string;
	activation_date: string;
	transfer_whole_balance: boolean;
	transfer_amount: number;
	status: 'SUCCESSFUL' | 'FAILED';
	rollover_result: 'SUCCESSFUL' | 'FAILED';
}

export interface VerificationResponse {
	data?: {
		/**
		 * List of verification records
		 */
		verification?: VerificationItem[];
	};
}

export interface VerificationItem {
	/**
	 * Unique ID assigned to the member at AustralianSuper
	 */
	party_type_id?: string;
	/**
	 * Communication channel used for verification (e.g., Phone, Email)
	 */
	channel?: string;
	/**
	 * Indicates if verification was successful
	 */
	verification?: boolean;
	/**
	 * Source system or app from which the verification originated
	 */
	source?: string;
	/**
	 * Expiry datetime of member verification
	 */
	expiry_timestamp?: string;
	/**
	 * Timestamp of the verification event
	 */
	timestamp?: string;
	/**
	 * Verified data, such as phone number or email address
	 */
	data?: string;
}

export interface VerificationStatus {
	data?: VerificationData;
}

export interface VerificationData {
	/**
	 * auth token of a validated OTP code
	 */
	auth_code: string;
}

export interface CustomOperationVerificationStatus {
	data?: CustomOperationVerificationData;
}

export interface CustomOperationVerificationData {
	/**
	 * Verified data, such as phone number or email address
	 */
	data: string;
	/**
	 * Communication channel used for verification (e.g., MOBILE, EMAIL)
	 */
	channel: 'MOBILE' | 'EMAIL';
}

export interface VerificationDetailsResponse {
	data?: VerificationDetailsResponseData;
}

export interface VerificationDetailsResponseData {
	/**
	 * Verification process type
	 */
	process_type?: 'FULL_IDV' | 'STEP_UP';
	verification_details?: {
		is_completed?: boolean;
		valid_until?: string;
		is_locked?: boolean;
		locked_until?: string;
	};
}

export interface InitiateVerificationRequest {
	data?: InitiateVerificationRequestData;
}

export interface InitiateVerificationRequestData {
	member_details?: {
		given_name?: string;
		last_name?: string;
		date_of_birth?: string;
	};
	form_type:
		| 'SECURITY_VERIFICATION'
		| 'BANK_ACCOUNT'
		| 'UPDATE_MOBILE_NUMBER'
		| 'WITHDRAWAL_UNRESTRICTED_NON_PRESERVED'
		| 'WITHDRAWAL_REACHED_PRESERVATION_AGE'
		| 'WITHDRAWAL_TRANSFER_TO_SMSF'
		| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_A'
		| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_B';
	consent: string;
	verification_details?: {
		process_type: 'FULL_IDV' | 'STEP_UP';
		channel: string;
		is_completed: boolean;
	};
	sso_token?: string;
}

export interface InitiateVerificationResponse {
	data?: InitiateVerificationResponseData;
}

export interface InitiateVerificationResponseData {
	application_number?: string;
	verification_id?: string;
	redirect_url?: string;
}

export interface VerificationResultsResponse {
	data?: VerificationResultsResponseData;
}

export interface VerificationResultsResponseData {
	outcome?: {
		result?: string;
		failed_reason?: string;
	};
	verification_details?: {
		valid_until?: string;
		locked_until?: string;
	};
}

export interface UpdateBankAccountRequest {
	data?: UpdateBankAccountRequestData;
}

export interface UpdateBankAccountRequestData {
	sso_token: string;
	bank_account_details?: {
		bsb: string;
		account_number: string;
		account_name: string;
	};
	verification_details?: {
		application_number: string;
		verification_id: string;
	};
}

export interface UpdateBankAccountResponse {
	data?: UpdateBankAccountResponseData;
}

export interface UpdateBankAccountResponseData {
	form_id?: string;
	form_status?: string;
	receipt_number?: string;
}

export interface WithdrawalRequest {
	data: WithdrawalRequestData;
}

export interface WithdrawalRequestData {
	sso_token: string;
	forms: WithdrawalForms;
	verification_details: WithdrawalVerificationDetails;
}

export interface WithdrawalForms {
	form_name: WithdrawalFormName;
	form_status: WithdrawalFormStatus;
	/**
	 * JSON form payload for the selected withdrawal form.
	 */
	form_data: string;
	declaration: boolean;
}

export type WithdrawalFormName =
	| 'WITHDRAWAL_UNRESTRICTED_NON_PRESERVED'
	| 'WITHDRAWAL_REACHED_PRESERVATION_AGE'
	| 'WITHDRAWAL_TRANSFER_TO_ANOTHER_FUND'
	| 'WITHDRAWAL_TRANSFER_TO_SMSF'
	| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_A'
	| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_B';

export type WithdrawalFormStatus = 'SUBMITTED';

export interface WithdrawalVerificationDetails {
	application_number: string;
	verification_id: string;
}

export interface WithdrawalResponse {
	data: WithdrawalResponseData;
}

export interface WithdrawalResponseData {
	form_id: string;
	receipt_number: string;
}

export interface ReferenceDataSuperFunds {
	data?: ReferenceDataSuperFundsData;
	meta?: Meta;
}

export interface ReferenceDataSuperFundsData {
	values?: ReferenceDataSuperFundsDataValues[];
}

export interface ReferenceDataSuperFundsDataValues {
	eRollover?: boolean;
	/**
	 * Unique Superannuation Identifier. Note usi and spin are used interchangibly and either of them can be empty
	 */
	usi?: string;
	fund_code?: string;
	/**
	 * flag to identify if the fund is registered with super stream
	 */
	superstream_registered_flag?: boolean;
	/**
	 * Australian Business Number
	 */
	abn: string;
	/**
	 * number identifying superannuation fund
	 */
	superannuation_fund_number: string;
	/**
	 * short description of the superannuation fund
	 */
	short_description?: string;
	/**
	 * name of the superannuation fund
	 */
	name: string;
	/**
	 * name of the body regulating the fund. Possible values are APRA - Australian Prudential Regulation Authority
	 */
	regulator?: string;
	/**
	 * Super Product Identifier Number. Note usi and spin are used interchangibly and either of them can be empty
	 */
	spin?: string;
	/**
	 * ACTIVE or INACTIVE
	 */
	fund_status?: string;
}

export interface SMSFFunds {
	data?: SMSFFundsData;
	meta?: Meta;
}

export interface SMSFFundsData {
	values?: SMSFFundsDataValues[];
}

export interface SMSFFundsDataValues {
	/**
	 * number identifying smsf provider
	 */
	id: string;
	/**
	 * electronic service address
	 */
	esi_alias: string;
	/**
	 * name of the smsf message provider
	 */
	name: string;
}

/**
 * Used to store metadata of a pagination.
 */
export interface Meta {
	/**
	 * The total number of records for this object
	 */
	total_records?: number;
	/**
	 * The total number of pages for this object
	 */
	total_pages?: number;
}

export interface MemberActionsExpGetResponse {
	data?: MemberActionsExpGetData;
}

export interface MemberActionsExpGetData {
	member_actions?: MemberExpActions;
	account_actions?: AccountExpActions[];
}

export interface MemberExpActions {
	first_ia_navigation_visit?: boolean;
	ia_navigation_actions?: IaNavigationActions;
	first_web_portal_login?: boolean;
	review_your_personal_details?: boolean;
	add_your_mobile_number?: boolean;
	manage_your_beneficiaries?: boolean;
	communication_preference?: boolean;
	tell_your_employer_last_updated_date?: string;
	consolidate_your_super_last_updated_date?: string;
	make_contribution_last_updated_date?: string;
	verified_mobile_number?: boolean;
	verified_email_address?: boolean;
}

export interface IaNavigationActions {
	account_overview?: boolean;
	manage_your_profile?: boolean;
}

export interface AccountExpActions {
	account_number?: string;
	product_name?: string;
	source?: string;
	/**
	 * JSON-encoded string of account-level action flags
	 */
	actions?: string;
}

export interface BeneficiaryData {
	data?: BeneficiaryGroup;
	capabilities: BeneficiaryCapabilities;
}

/**
 * Member actions currently available for beneficiary nominations.
 */
export interface BeneficiaryCapabilities {
	/**
	 * Indicates whether the member can add beneficiary nominations. True when no beneficiary records exist; otherwise false.
	 */
	can_add: boolean;
	/**
	 * Indicates whether the member can edit existing beneficiary nominations. True when beneficiary_type is Nominated or BindingNonLapsing; otherwise false.
	 */
	can_edit: boolean;
	/**
	 * Indicates whether the member can cancel existing beneficiary nominations. True when beneficiary_type is BindingNonLapsing; otherwise false.
	 */
	can_cancel: boolean;
}

export interface BeneficiaryGroup {
	expiry_date?: string;
	beneficiary_type?: string;
	member_beneficiary_details?: Beneficiary[];
	effective_date?: string;
}

export interface BeneficiariesUpdate {
	data?: BeneficiariesUpdateObject;
}

export interface BeneficiariesUpdateObject {
	/**
	 * Auth token from OTP validation
	 */
	auth_code: string;
	beneficiary_type: string;
	member_beneficiary_details: Beneficiary[];
}

export interface Beneficiary {
	gender: string;
	given_names?: string;
	family_name?: string;
	relationship_code?: string;
	percentage?: number;
	payment_option_type: string;
	phone_number?: string;
	address?: BeneficiaryAddress;
}

export interface BeneficiaryAddress {
	type?: string;
	address_line1?: string;
	address_line2?: string;
	city?: string;
	state?: string;
	postcode?: string;
	country?: string;
	status?: string;
}

export interface BeneficiariesDelete {
	data?: {
		/**
		 * Auth token from OTP validation
		 */
		auth_code: string;
	};
}

export interface GetDirectDebitEligibilityResponse {
	data: GetDirectDebitEligibilityResponseData;
}

export interface GetDirectDebitEligibilityResponseData {
	eligibility: DirectDebitEligibility;
}

export interface DirectDebitEligibility {
	is_eligible: boolean;
	not_eligible_reason?: string;
	reason_data?:
		| DirectDebitEligibilityReasonData
		| DirectDebitEligibilityWaitPeriodReasonData;
}

export interface DirectDebitEligibilityReasonData {
	eligible_date: string;
	transactions: DirectDebitEligibilityTransaction[];
}

export interface DirectDebitEligibilityWaitPeriodReasonData {
	bank_institution: string;
	bank_bsb: string;
	bank_account_number: string;
	days_to_wait: number;
}

export interface DirectDebitEligibilityTransaction {
	transaction_amount: number;
	transaction_timestamp: string;
	bank_institution: string;
	bank_bsb: string;
	bank_account_number: string;
}

/**
 * Update successful. No Content
 */
export type Response204 = void;

/**
 * Bad request
 */
export type Response400 = Error;

/**
 * Unauthorized
 */
export type Response401 = Error;

/**
 * Forbidden
 */
export type Response403 = Error;

/**
 * Not found
 */
export type Response404 = Error;

/**
 * Conflict
 */
export type Response409 = Error;

/**
 * Unprocessable Entity
 */
export type Response422 = Error;

/**
 * Internal server error
 */
export type Response500 = Error;

/**
 * Service unavailable error
 */
export type Response503 = Error;

/**
 * An error has occurred
 */
export type ErrorResponseResponse = Error;

export interface GetCacheWarmingQueryParams {
	/**
	 * Financial year start date.
	 */
	fy_start_date: string;
	/**
	 * Financial year end date.
	 */
	fy_end_date: string;
}

export interface GetCacheWarmingPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetCacheWarmingProps = Omit<
	GetProps<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetCacheWarmingQueryParams,
		GetCacheWarmingPathParams
	>,
	'path'
> &
	GetCacheWarmingPathParams;

/**
 * retrieves list of account transactions from domain layer
 *
 *
 * Consume various endpoints to load data to Redis Cache
 */
export const GetCacheWarming = ({
	product_name,
	account_number,
	...props
}: GetCacheWarmingProps) => (
	<Get<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetCacheWarmingQueryParams,
		GetCacheWarmingPathParams
	>
		path={`/cache-loaders/accounts/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetCacheWarmingProps = Omit<
	UseGetProps<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetCacheWarmingQueryParams,
		GetCacheWarmingPathParams
	>,
	'path'
> &
	GetCacheWarmingPathParams;

/**
 * retrieves list of account transactions from domain layer
 *
 *
 * Consume various endpoints to load data to Redis Cache
 */
export const useGetCacheWarming = ({
	product_name,
	account_number,
	...props
}: UseGetCacheWarmingProps) =>
	useGet<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetCacheWarmingQueryParams,
		GetCacheWarmingPathParams
	>(
		(paramsInPath: GetCacheWarmingPathParams) =>
			`/cache-loaders/accounts/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface GetTransactionsQueryParams {
	/**
	 * The date range of the contribution made.This field represents the from date. ISO8601 format.
	 */
	from_date?: string;
	/**
	 * The date range of the contribution made. This field represents the to date. ISO8601 format.
	 */
	to_date?: string;
	/**
	 * comma separated values of transaction type.
	 */
	transaction_types?: TransactionTypes[];
	/**
	 * Item result offset. Used in conjunction with limit.
	 */
	offset?: ItemOffset;
	/**
	 * Maximum items to return.
	 */
	limit?: ItemLimit;
}

export interface GetTransactionsPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetTransactionsProps = Omit<
	GetProps<
		Transaction,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTransactionsQueryParams,
		GetTransactionsPathParams
	>,
	'path'
> &
	GetTransactionsPathParams;

/**
 * Get account transactions
 *
 * Retrieves transactions for an account through transaction domain private endpoint.
 */
export const GetTransactions = ({
	product_name,
	account_number,
	...props
}: GetTransactionsProps) => (
	<Get<
		Transaction,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTransactionsQueryParams,
		GetTransactionsPathParams
	>
		path={`/accounts/${product_name}/${account_number}/transactions`}
		{...props}
	/>
);

export type UseGetTransactionsProps = Omit<
	UseGetProps<
		Transaction,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTransactionsQueryParams,
		GetTransactionsPathParams
	>,
	'path'
> &
	GetTransactionsPathParams;

/**
 * Get account transactions
 *
 * Retrieves transactions for an account through transaction domain private endpoint.
 */
export const useGetTransactions = ({
	product_name,
	account_number,
	...props
}: UseGetTransactionsProps) =>
	useGet<
		Transaction,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTransactionsQueryParams,
		GetTransactionsPathParams
	>(
		(paramsInPath: GetTransactionsPathParams) =>
			`/accounts/${paramsInPath.product_name}/${paramsInPath.account_number}/transactions`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export type GetServiceStatusProps = Omit<
	GetProps<
		ServiceStatusResult,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Get service status
 *
 * Get service status to retrieve online or offline status for 3rd party applications. Currently only LINK status is supported, when the value of LINK_STATUS key is offline, sitecore will turn resilience mode on.
 *
 */
export const GetServiceStatus = (props: GetServiceStatusProps) => (
	<Get<
		ServiceStatusResult,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>
		path={`/members/service-status`}
		{...props}
	/>
);

export type UseGetServiceStatusProps = Omit<
	UseGetProps<
		ServiceStatusResult,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Get service status
 *
 * Get service status to retrieve online or offline status for 3rd party applications. Currently only LINK status is supported, when the value of LINK_STATUS key is offline, sitecore will turn resilience mode on.
 *
 */
export const useGetServiceStatus = (props: UseGetServiceStatusProps) =>
	useGet<
		ServiceStatusResult,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>(`/members/service-status`, props);

export interface UpdateServiceStatusPathParams {
	/**
	 * Unique key for the service status
	 */
	key: string;
}

export type UpdateServiceStatusProps = Omit<
	MutateProps<
		void,
		Error,
		void,
		ServiceStatusUpdate,
		UpdateServiceStatusPathParams
	>,
	'path' | 'verb'
> &
	UpdateServiceStatusPathParams;

/**
 * Update service status
 *
 * Update service status to update online or offline status for 3rd party applications. Currently only LINK status is supported, when the value of LINK_STATUS key is offline, resilience mode is on.
 *
 */
export const UpdateServiceStatus = ({
	key,
	...props
}: UpdateServiceStatusProps) => (
	<Mutate<void, Error, void, ServiceStatusUpdate, UpdateServiceStatusPathParams>
		verb="PUT"
		path={`/members/service-status/${key}`}
		{...props}
	/>
);

export type UseUpdateServiceStatusProps = Omit<
	UseMutateProps<
		void,
		Error,
		void,
		ServiceStatusUpdate,
		UpdateServiceStatusPathParams
	>,
	'path' | 'verb'
> &
	UpdateServiceStatusPathParams;

/**
 * Update service status
 *
 * Update service status to update online or offline status for 3rd party applications. Currently only LINK status is supported, when the value of LINK_STATUS key is offline, resilience mode is on.
 *
 */
export const useUpdateServiceStatus = ({
	key,
	...props
}: UseUpdateServiceStatusProps) =>
	useMutate<
		void,
		Error,
		void,
		ServiceStatusUpdate,
		UpdateServiceStatusPathParams
	>(
		'PUT',
		(paramsInPath: UpdateServiceStatusPathParams) =>
			`/members/service-status/${paramsInPath.key}`,
		{ pathParams: { key }, ...props },
	);

export type GetMemberProps = Omit<
	GetProps<
		ExpMemberData,
		Response401 | Response404 | Response500 | ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Get member
 */
export const GetMember = (props: GetMemberProps) => (
	<Get<
		ExpMemberData,
		Response401 | Response404 | Response500 | ErrorResponseResponse,
		void,
		void
	>
		path={`/member-details`}
		{...props}
	/>
);

export type UseGetMemberProps = Omit<
	UseGetProps<
		ExpMemberData,
		Response401 | Response404 | Response500 | ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Get member
 */
export const useGetMember = (props: UseGetMemberProps) =>
	useGet<
		ExpMemberData,
		Response401 | Response404 | Response500 | ErrorResponseResponse,
		void,
		void
	>(`/member-details`, props);

export type UpdateMobileProps = Omit<
	MutateProps<
		UpdateMobileResponse | void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberMobileData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Update member mobile number
 */
export const UpdateMobile = (props: UpdateMobileProps) => (
	<Mutate<
		UpdateMobileResponse | void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberMobileData,
		void
	>
		verb="PUT"
		path={`/member-details/mobile`}
		{...props}
	/>
);

export type UseUpdateMobileProps = Omit<
	UseMutateProps<
		UpdateMobileResponse | void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberMobileData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Update member mobile number
 */
export const useUpdateMobile = (props: UseUpdateMobileProps) =>
	useMutate<
		UpdateMobileResponse | void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberMobileData,
		void
	>('PUT', `/member-details/mobile`, props);

export type UpdateHomeWorkProps = Omit<
	MutateProps<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberHomeWorkData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Update member Home/Work number
 */
export const UpdateHomeWork = (props: UpdateHomeWorkProps) => (
	<Mutate<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberHomeWorkData,
		void
	>
		verb="PUT"
		path={`/member-details/phone-numbers/home-work`}
		{...props}
	/>
);

export type UseUpdateHomeWorkProps = Omit<
	UseMutateProps<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberHomeWorkData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Update member Home/Work number
 */
export const useUpdateHomeWork = (props: UseUpdateHomeWorkProps) =>
	useMutate<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		MemberHomeWorkData,
		void
	>('PUT', `/member-details/phone-numbers/home-work`, props);

export interface GetProfileActionsPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetProfileActionsProps = Omit<
	GetProps<
		ExpMemberProfileActionsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetProfileActionsPathParams
	>,
	'path'
> &
	GetProfileActionsPathParams;

/**
 * Get profile actions
 */
export const GetProfileActions = ({
	product_name,
	account_number,
	...props
}: GetProfileActionsProps) => (
	<Get<
		ExpMemberProfileActionsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetProfileActionsPathParams
	>
		path={`/profile-setup-actions/accounts/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetProfileActionsProps = Omit<
	UseGetProps<
		ExpMemberProfileActionsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetProfileActionsPathParams
	>,
	'path'
> &
	GetProfileActionsPathParams;

/**
 * Get profile actions
 */
export const useGetProfileActions = ({
	product_name,
	account_number,
	...props
}: UseGetProfileActionsProps) =>
	useGet<
		ExpMemberProfileActionsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetProfileActionsPathParams
	>(
		(paramsInPath: GetProfileActionsPathParams) =>
			`/profile-setup-actions/accounts/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export type NotifySpouseProps = Omit<
	MutateProps<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpSpouseContributionData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Notify Spouse contribution
 */
export const NotifySpouse = (props: NotifySpouseProps) => (
	<Mutate<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpSpouseContributionData,
		void
	>
		verb="POST"
		path={`/members/spouse-contribution`}
		{...props}
	/>
);

export type UseNotifySpouseProps = Omit<
	UseMutateProps<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpSpouseContributionData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Notify Spouse contribution
 */
export const useNotifySpouse = (props: UseNotifySpouseProps) =>
	useMutate<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpSpouseContributionData,
		void
	>('POST', `/members/spouse-contribution`, props);

export type NotifyEmployerProps = Omit<
	MutateProps<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpNotifyEmployerData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Notify Employer
 */
export const NotifyEmployer = (props: NotifyEmployerProps) => (
	<Mutate<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpNotifyEmployerData,
		void
	>
		verb="POST"
		path={`/members/employer-notification`}
		{...props}
	/>
);

export type UseNotifyEmployerProps = Omit<
	UseMutateProps<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpNotifyEmployerData,
		void
	>,
	'path' | 'verb'
>;

/**
 * Notify Employer
 */
export const useNotifyEmployer = (props: UseNotifyEmployerProps) =>
	useMutate<
		void,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		ExpNotifyEmployerData,
		void
	>('POST', `/members/employer-notification`, props);

export interface SubmitRolloverRequestPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type SubmitRolloverRequestProps = Omit<
	MutateProps<
		RolloverFundResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		RolloverFundRequest,
		SubmitRolloverRequestPathParams
	>,
	'path' | 'verb'
> &
	SubmitRolloverRequestPathParams;

/**
 * Submits Rollover Request to transfer money into Member's account from another source fund.
 */
export const SubmitRolloverRequest = ({
	product_name,
	account_number,
	...props
}: SubmitRolloverRequestProps) => (
	<Mutate<
		RolloverFundResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		RolloverFundRequest,
		SubmitRolloverRequestPathParams
	>
		verb="POST"
		path={`/members/rollovers/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseSubmitRolloverRequestProps = Omit<
	UseMutateProps<
		RolloverFundResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		RolloverFundRequest,
		SubmitRolloverRequestPathParams
	>,
	'path' | 'verb'
> &
	SubmitRolloverRequestPathParams;

/**
 * Submits Rollover Request to transfer money into Member's account from another source fund.
 */
export const useSubmitRolloverRequest = ({
	product_name,
	account_number,
	...props
}: UseSubmitRolloverRequestProps) =>
	useMutate<
		RolloverFundResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		RolloverFundRequest,
		SubmitRolloverRequestPathParams
	>(
		'POST',
		(paramsInPath: SubmitRolloverRequestPathParams) =>
			`/members/rollovers/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export type GetVerificationStatusProps = Omit<
	GetProps<
		VerificationResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Retrieve the verification statuses of a member’s email address and mobile number
 */
export const GetVerificationStatus = (props: GetVerificationStatusProps) => (
	<Get<
		VerificationResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>
		path={`/members/contact-verifications`}
		{...props}
	/>
);

export type UseGetVerificationStatusProps = Omit<
	UseGetProps<
		VerificationResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Retrieve the verification statuses of a member’s email address and mobile number
 */
export const useGetVerificationStatus = (
	props: UseGetVerificationStatusProps,
) =>
	useGet<
		VerificationResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		void
	>(`/members/contact-verifications`, props);

export interface UpdateVerificationStatusPathParams {
	/**
	 * Channel identifier.
	 */
	channel: 'MOBILE' | 'EMAIL';
}

export type UpdateVerificationStatusProps = Omit<
	MutateProps<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		VerificationStatus,
		UpdateVerificationStatusPathParams
	>,
	'path' | 'verb'
> &
	UpdateVerificationStatusPathParams;

/**
 * Update the verification status of a member’s email address or mobile number to confirm ownership after successful validation
 */
export const UpdateVerificationStatus = ({
	channel,
	...props
}: UpdateVerificationStatusProps) => (
	<Mutate<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		VerificationStatus,
		UpdateVerificationStatusPathParams
	>
		verb="PUT"
		path={`/members/contact-verifications/${channel}`}
		{...props}
	/>
);

export type UseUpdateVerificationStatusProps = Omit<
	UseMutateProps<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		VerificationStatus,
		UpdateVerificationStatusPathParams
	>,
	'path' | 'verb'
> &
	UpdateVerificationStatusPathParams;

/**
 * Update the verification status of a member’s email address or mobile number to confirm ownership after successful validation
 */
export const useUpdateVerificationStatus = ({
	channel,
	...props
}: UseUpdateVerificationStatusProps) =>
	useMutate<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		VerificationStatus,
		UpdateVerificationStatusPathParams
	>(
		'PUT',
		(paramsInPath: UpdateVerificationStatusPathParams) =>
			`/members/contact-verifications/${paramsInPath.channel}`,
		{ pathParams: { channel }, ...props },
	);

export type ConfirmVerificationStatusProps = Omit<
	MutateProps<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		CustomOperationVerificationStatus,
		void
	>,
	'path' | 'verb'
>;

/**
 * Update the expiry and timestamp of a member’s email address or mobile number
 */
export const ConfirmVerificationStatus = (
	props: ConfirmVerificationStatusProps,
) => (
	<Mutate<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		CustomOperationVerificationStatus,
		void
	>
		verb="POST"
		path={`/operations/members/confirm-contact`}
		{...props}
	/>
);

export type UseConfirmVerificationStatusProps = Omit<
	UseMutateProps<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		CustomOperationVerificationStatus,
		void
	>,
	'path' | 'verb'
>;

/**
 * Update the expiry and timestamp of a member’s email address or mobile number
 */
export const useConfirmVerificationStatus = (
	props: UseConfirmVerificationStatusProps,
) =>
	useMutate<
		Response204,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		CustomOperationVerificationStatus,
		void
	>('POST', `/operations/members/confirm-contact`, props);

export type GetExternalRedirectUriProps = Omit<
	MutateProps<
		TalRedirectUri,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		TalAccountLookup,
		void
	>,
	'path' | 'verb'
>;

/**
 * Retrieve TAL redirect URI with GUID
 */
export const GetExternalRedirectUri = (props: GetExternalRedirectUriProps) => (
	<Mutate<
		TalRedirectUri,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		TalAccountLookup,
		void
	>
		verb="POST"
		path={`/tal/account-external-lookup`}
		{...props}
	/>
);

export type UseGetExternalRedirectUriProps = Omit<
	UseMutateProps<
		TalRedirectUri,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		TalAccountLookup,
		void
	>,
	'path' | 'verb'
>;

/**
 * Retrieve TAL redirect URI with GUID
 */
export const useGetExternalRedirectUri = (
	props: UseGetExternalRedirectUriProps,
) =>
	useMutate<
		TalRedirectUri,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		TalAccountLookup,
		void
	>('POST', `/tal/account-external-lookup`, props);

export interface GetAccountSnapshotPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetAccountSnapshotProps = Omit<
	GetProps<
		AccountSnapshot,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetAccountSnapshotPathParams
	>,
	'path'
> &
	GetAccountSnapshotPathParams;

/**
 * Get Account Snapshot
 *
 * This public endpoint will provide the transaction summary of five financial years which will be used to show the Account Snapshot Dashboard.
 */
export const GetAccountSnapshot = ({
	product_name,
	account_number,
	...props
}: GetAccountSnapshotProps) => (
	<Get<
		AccountSnapshot,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetAccountSnapshotPathParams
	>
		path={`/components/account-snapshot/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetAccountSnapshotProps = Omit<
	UseGetProps<
		AccountSnapshot,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetAccountSnapshotPathParams
	>,
	'path'
> &
	GetAccountSnapshotPathParams;

/**
 * Get Account Snapshot
 *
 * This public endpoint will provide the transaction summary of five financial years which will be used to show the Account Snapshot Dashboard.
 */
export const useGetAccountSnapshot = ({
	product_name,
	account_number,
	...props
}: UseGetAccountSnapshotProps) =>
	useGet<
		AccountSnapshot,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetAccountSnapshotPathParams
	>(
		(paramsInPath: GetAccountSnapshotPathParams) =>
			`/components/account-snapshot/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface GetInvestmentsPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetInvestmentsProps = Omit<
	GetProps<
		InvestmentsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetInvestmentsPathParams
	>,
	'path'
> &
	GetInvestmentsPathParams;

/**
 * Get Investments
 *
 * This public endpoint returns Dashboard Investments component data for the selected account, including investment summary, allocation, performance, pending switch status, and account flags.
 */
export const GetInvestments = ({
	product_name,
	account_number,
	...props
}: GetInvestmentsProps) => (
	<Get<
		InvestmentsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetInvestmentsPathParams
	>
		path={`/components/investments/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetInvestmentsProps = Omit<
	UseGetProps<
		InvestmentsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetInvestmentsPathParams
	>,
	'path'
> &
	GetInvestmentsPathParams;

/**
 * Get Investments
 *
 * This public endpoint returns Dashboard Investments component data for the selected account, including investment summary, allocation, performance, pending switch status, and account flags.
 */
export const useGetInvestments = ({
	product_name,
	account_number,
	...props
}: UseGetInvestmentsProps) =>
	useGet<
		InvestmentsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetInvestmentsPathParams
	>(
		(paramsInPath: GetInvestmentsPathParams) =>
			`/components/investments/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface GetEligibiltyFipQueryParams {
	/**
	 * Eligibility topic
	 */
	topic?: string;
}

export interface GetEligibiltyFipPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetEligibiltyFipProps = Omit<
	GetProps<
		FipEligibiltyResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetEligibiltyFipQueryParams,
		GetEligibiltyFipPathParams
	>,
	'path'
> &
	GetEligibiltyFipPathParams;

/**
 * Facilitates the eligibility call from FED to FIP
 */
export const GetEligibiltyFip = ({
	product_name,
	account_number,
	...props
}: GetEligibiltyFipProps) => (
	<Get<
		FipEligibiltyResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetEligibiltyFipQueryParams,
		GetEligibiltyFipPathParams
	>
		path={`/components/simple-advice/accordion/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetEligibiltyFipProps = Omit<
	UseGetProps<
		FipEligibiltyResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetEligibiltyFipQueryParams,
		GetEligibiltyFipPathParams
	>,
	'path'
> &
	GetEligibiltyFipPathParams;

/**
 * Facilitates the eligibility call from FED to FIP
 */
export const useGetEligibiltyFip = ({
	product_name,
	account_number,
	...props
}: UseGetEligibiltyFipProps) =>
	useGet<
		FipEligibiltyResponse,
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetEligibiltyFipQueryParams,
		GetEligibiltyFipPathParams
	>(
		(paramsInPath: GetEligibiltyFipPathParams) =>
			`/components/simple-advice/accordion/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface GetTrustedDeviceQueryParams {
	/**
	 * Timestamp referring to previous login.
	 */
	last_login?: string;
}

export type GetTrustedDeviceProps = Omit<
	GetProps<
		TrustedDevicesResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTrustedDeviceQueryParams,
		void
	>,
	'path'
>;

/**
 * Get list of trusted devices
 *
 * Returns all trusted devices for the authenticated user, excluding the current device if it is trusted.
 */
export const GetTrustedDevice = (props: GetTrustedDeviceProps) => (
	<Get<
		TrustedDevicesResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTrustedDeviceQueryParams,
		void
	>
		path={`/members/trusted-devices`}
		{...props}
	/>
);

export type UseGetTrustedDeviceProps = Omit<
	UseGetProps<
		TrustedDevicesResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTrustedDeviceQueryParams,
		void
	>,
	'path'
>;

/**
 * Get list of trusted devices
 *
 * Returns all trusted devices for the authenticated user, excluding the current device if it is trusted.
 */
export const useGetTrustedDevice = (props: UseGetTrustedDeviceProps) =>
	useGet<
		TrustedDevicesResponse,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetTrustedDeviceQueryParams,
		void
	>(`/members/trusted-devices`, props);

export type DeleteTrustedDeviceProps = Omit<
	MutateProps<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		DeleteTrustedDeviceRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Removes one or more trusted device/s
 *
 * Deletes one or more trusted devices for the authenticated user.
 */
export const DeleteTrustedDevice = (props: DeleteTrustedDeviceProps) => (
	<Mutate<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		DeleteTrustedDeviceRequest,
		void
	>
		verb="POST"
		path={`/members/batch-operations/remove-trusted-devices`}
		{...props}
	/>
);

export type UseDeleteTrustedDeviceProps = Omit<
	UseMutateProps<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		DeleteTrustedDeviceRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Removes one or more trusted device/s
 *
 * Deletes one or more trusted devices for the authenticated user.
 */
export const useDeleteTrustedDevice = (props: UseDeleteTrustedDeviceProps) =>
	useMutate<
		void,
		| Response400
		| Response401
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		DeleteTrustedDeviceRequest,
		void
	>('POST', `/members/batch-operations/remove-trusted-devices`, props);

export type CreateOtpProps = Omit<
	MutateProps<
		GenerateOtpResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		void,
		GenerateOtpRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Create an OTP code
 */
export const CreateOtp = (props: CreateOtpProps) => (
	<Mutate<
		GenerateOtpResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		void,
		GenerateOtpRequest,
		void
	>
		verb="POST"
		path={`/members/otps`}
		{...props}
	/>
);

export type UseCreateOtpProps = Omit<
	UseMutateProps<
		GenerateOtpResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		void,
		GenerateOtpRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Create an OTP code
 */
export const useCreateOtp = (props: UseCreateOtpProps) =>
	useMutate<
		GenerateOtpResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		void,
		GenerateOtpRequest,
		void
	>('POST', `/members/otps`, props);

export type ConfirmOtpProps = Omit<
	MutateProps<
		ValidateOtpResponse,
		Response400 | Response401 | Error | Response500 | ErrorResponseResponse,
		void,
		ValidateOtpRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Validate the OTP code retrieved from email or mobile
 */
export const ConfirmOtp = (props: ConfirmOtpProps) => (
	<Mutate<
		ValidateOtpResponse,
		Response400 | Response401 | Error | Response500 | ErrorResponseResponse,
		void,
		ValidateOtpRequest,
		void
	>
		verb="PUT"
		path={`/members/otps/validate`}
		{...props}
	/>
);

export type UseConfirmOtpProps = Omit<
	UseMutateProps<
		ValidateOtpResponse,
		Response400 | Response401 | Error | Response500 | ErrorResponseResponse,
		void,
		ValidateOtpRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Validate the OTP code retrieved from email or mobile
 */
export const useConfirmOtp = (props: UseConfirmOtpProps) =>
	useMutate<
		ValidateOtpResponse,
		Response400 | Response401 | Error | Response500 | ErrorResponseResponse,
		void,
		ValidateOtpRequest,
		void
	>('PUT', `/members/otps/validate`, props);

export interface GetSuperFundsQueryParams {
	usi?: string;
	/**
	 * Name of the fund. Required for APRA search
	 */
	fund_name?: string;
	/**
	 * Australian business number. Required For SMSF search
	 */
	abn?: string;
	/**
	 * Default Value is APRA
	 */
	fund_type?: 'ATO' | 'APRA' | 'ALL';
	/**
	 * To specify which fund status to retrieve
	 */
	fund_status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
}

export type GetSuperFundsProps = Omit<
	GetProps<
		ReferenceDataSuperFunds,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		GetSuperFundsQueryParams,
		void
	>,
	'path'
>;

/**
 * Used to search for Superannuation Funds as potential sources for Rollovers.
 */
export const GetSuperFunds = (props: GetSuperFundsProps) => (
	<Get<
		ReferenceDataSuperFunds,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		GetSuperFundsQueryParams,
		void
	>
		path={`/members/datasets/funds`}
		{...props}
	/>
);

export type UseGetSuperFundsProps = Omit<
	UseGetProps<
		ReferenceDataSuperFunds,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		GetSuperFundsQueryParams,
		void
	>,
	'path'
>;

/**
 * Used to search for Superannuation Funds as potential sources for Rollovers.
 */
export const useGetSuperFunds = (props: UseGetSuperFundsProps) =>
	useGet<
		ReferenceDataSuperFunds,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		GetSuperFundsQueryParams,
		void
	>(`/members/datasets/funds`, props);

export type GetSmsfFundsProps = Omit<
	GetProps<
		SMSFFunds,
		Response401 | Response500 | ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Used to search for a SMSF super funds
 */
export const GetSmsfFunds = (props: GetSmsfFundsProps) => (
	<Get<SMSFFunds, Response401 | Response500 | ErrorResponseResponse, void, void>
		path={`/members/datasets/smsf-funds`}
		{...props}
	/>
);

export type UseGetSmsfFundsProps = Omit<
	UseGetProps<
		SMSFFunds,
		Response401 | Response500 | ErrorResponseResponse,
		void,
		void
	>,
	'path'
>;

/**
 * Used to search for a SMSF super funds
 */
export const useGetSmsfFunds = (props: UseGetSmsfFundsProps) =>
	useGet<
		SMSFFunds,
		Response401 | Response500 | ErrorResponseResponse,
		void,
		void
	>(`/members/datasets/smsf-funds`, props);

export interface GetIdentityVerificationInfoQueryParams {
	/**
	 * Identity verification form type
	 */
	form_type:
		| 'SECURITY_VERIFICATION'
		| 'BANK_ACCOUNT'
		| 'UPDATE_MOBILE_NUMBER'
		| 'WITHDRAWAL_UNRESTRICTED_NON_PRESERVED'
		| 'WITHDRAWAL_REACHED_PRESERVATION_AGE'
		| 'WITHDRAWAL_TRANSFER_TO_SMSF'
		| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_A'
		| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_B';
}

export type GetIdentityVerificationInfoProps = Omit<
	GetProps<
		VerificationDetailsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationInfoQueryParams,
		void
	>,
	'path'
>;

/**
 * Retrieve identity verification info for the requested form type
 *
 * Returns verification process type and verification details for the specified form type.
 */
export const GetIdentityVerificationInfo = (
	props: GetIdentityVerificationInfoProps,
) => (
	<Get<
		VerificationDetailsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationInfoQueryParams,
		void
	>
		path={`/components/identity-verifications/info`}
		{...props}
	/>
);

export type UseGetIdentityVerificationInfoProps = Omit<
	UseGetProps<
		VerificationDetailsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationInfoQueryParams,
		void
	>,
	'path'
>;

/**
 * Retrieve identity verification info for the requested form type
 *
 * Returns verification process type and verification details for the specified form type.
 */
export const useGetIdentityVerificationInfo = (
	props: UseGetIdentityVerificationInfoProps,
) =>
	useGet<
		VerificationDetailsResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationInfoQueryParams,
		void
	>(`/components/identity-verifications/info`, props);

export type InitiateIdentityVerificationProps = Omit<
	MutateProps<
		InitiateVerificationResponse,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		InitiateVerificationRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Initiate an identity verification process
 */
export const InitiateIdentityVerification = (
	props: InitiateIdentityVerificationProps,
) => (
	<Mutate<
		InitiateVerificationResponse,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		InitiateVerificationRequest,
		void
	>
		verb="POST"
		path={`/components/identity-verifications/initiate`}
		{...props}
	/>
);

export type UseInitiateIdentityVerificationProps = Omit<
	UseMutateProps<
		InitiateVerificationResponse,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		InitiateVerificationRequest,
		void
	>,
	'path' | 'verb'
>;

/**
 * Initiate an identity verification process
 */
export const useInitiateIdentityVerification = (
	props: UseInitiateIdentityVerificationProps,
) =>
	useMutate<
		InitiateVerificationResponse,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		InitiateVerificationRequest,
		void
	>('POST', `/components/identity-verifications/initiate`, props);

export interface GetIdentityVerificationResultsQueryParams {
	/**
	 * Identity verification form type
	 */
	form_type:
		| 'SECURITY_VERIFICATION'
		| 'BANK_ACCOUNT'
		| 'UPDATE_MOBILE_NUMBER'
		| 'WITHDRAWAL_UNRESTRICTED_NON_PRESERVED'
		| 'WITHDRAWAL_REACHED_PRESERVATION_AGE'
		| 'WITHDRAWAL_TRANSFER_TO_SMSF'
		| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_A'
		| 'WITHDRAWAL_FINANCIAL_HARDSHIP_CLAIM_TYPE_B';
}

export interface GetIdentityVerificationResultsPathParams {
	/**
	 * Unique verification process identifier
	 */
	verification_id: string;
}

export type GetIdentityVerificationResultsProps = Omit<
	GetProps<
		VerificationResultsResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationResultsQueryParams,
		GetIdentityVerificationResultsPathParams
	>,
	'path'
> &
	GetIdentityVerificationResultsPathParams;

/**
 * Retrieve verification results for a verification process
 */
export const GetIdentityVerificationResults = ({
	verification_id,
	...props
}: GetIdentityVerificationResultsProps) => (
	<Get<
		VerificationResultsResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationResultsQueryParams,
		GetIdentityVerificationResultsPathParams
	>
		path={`/components/identity-verifications/${verification_id}/results`}
		{...props}
	/>
);

export type UseGetIdentityVerificationResultsProps = Omit<
	UseGetProps<
		VerificationResultsResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationResultsQueryParams,
		GetIdentityVerificationResultsPathParams
	>,
	'path'
> &
	GetIdentityVerificationResultsPathParams;

/**
 * Retrieve verification results for a verification process
 */
export const useGetIdentityVerificationResults = ({
	verification_id,
	...props
}: UseGetIdentityVerificationResultsProps) =>
	useGet<
		VerificationResultsResponse,
		| Response400
		| Response401
		| Response403
		| Response500
		| ErrorResponseResponse,
		GetIdentityVerificationResultsQueryParams,
		GetIdentityVerificationResultsPathParams
	>(
		(paramsInPath: GetIdentityVerificationResultsPathParams) =>
			`/components/identity-verifications/${paramsInPath.verification_id}/results`,
		{ pathParams: { verification_id }, ...props },
	);

export interface CancelIdentityVerificationPathParams {
	/**
	 * Unique verification process identifier
	 */
	verification_id: string;
}

export type CancelIdentityVerificationProps = Omit<
	MutateProps<
		void,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		void,
		CancelIdentityVerificationPathParams
	>,
	'path' | 'verb'
> &
	CancelIdentityVerificationPathParams;

/**
 * Cancel an identity verification process
 */
export const CancelIdentityVerification = ({
	verification_id,
	...props
}: CancelIdentityVerificationProps) => (
	<Mutate<
		void,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		void,
		CancelIdentityVerificationPathParams
	>
		verb="PUT"
		path={`/components/identity-verifications/${verification_id}/cancel`}
		{...props}
	/>
);

export type UseCancelIdentityVerificationProps = Omit<
	UseMutateProps<
		void,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		void,
		CancelIdentityVerificationPathParams
	>,
	'path' | 'verb'
> &
	CancelIdentityVerificationPathParams;

/**
 * Cancel an identity verification process
 */
export const useCancelIdentityVerification = ({
	verification_id,
	...props
}: UseCancelIdentityVerificationProps) =>
	useMutate<
		void,
		Response400 | Response401 | Response500 | ErrorResponseResponse,
		void,
		void,
		CancelIdentityVerificationPathParams
	>(
		'PUT',
		(paramsInPath: CancelIdentityVerificationPathParams) =>
			`/components/identity-verifications/${paramsInPath.verification_id}/cancel`,
		{ pathParams: { verification_id }, ...props },
	);

export interface UpdateBankAccountPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
	/**
	 * Bank account id
	 */
	bank_account_id: string;
}

export type UpdateBankAccountProps = Omit<
	MutateProps<
		UpdateBankAccountResponse,
		| Response400
		| Response401
		| Error
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		UpdateBankAccountRequest,
		UpdateBankAccountPathParams
	>,
	'path' | 'verb'
> &
	UpdateBankAccountPathParams;

/**
 * Update bank account details for a member account
 */
export const UpdateBankAccount = ({
	product_name,
	account_number,
	bank_account_id,
	...props
}: UpdateBankAccountProps) => (
	<Mutate<
		UpdateBankAccountResponse,
		| Response400
		| Response401
		| Error
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		UpdateBankAccountRequest,
		UpdateBankAccountPathParams
	>
		verb="PUT"
		path={`/accounts/${product_name}/${account_number}/bank-accounts/${bank_account_id}`}
		{...props}
	/>
);

export type UseUpdateBankAccountProps = Omit<
	UseMutateProps<
		UpdateBankAccountResponse,
		| Response400
		| Response401
		| Error
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		UpdateBankAccountRequest,
		UpdateBankAccountPathParams
	>,
	'path' | 'verb'
> &
	UpdateBankAccountPathParams;

/**
 * Update bank account details for a member account
 */
export const useUpdateBankAccount = ({
	product_name,
	account_number,
	bank_account_id,
	...props
}: UseUpdateBankAccountProps) =>
	useMutate<
		UpdateBankAccountResponse,
		| Response400
		| Response401
		| Error
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		UpdateBankAccountRequest,
		UpdateBankAccountPathParams
	>(
		'PUT',
		(paramsInPath: UpdateBankAccountPathParams) =>
			`/accounts/${paramsInPath.product_name}/${paramsInPath.account_number}/bank-accounts/${paramsInPath.bank_account_id}`,
		{ pathParams: { product_name, account_number, bank_account_id }, ...props },
	);

export interface SubmitWithdrawalPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type SubmitWithdrawalProps = Omit<
	MutateProps<
		WithdrawalResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		WithdrawalRequest,
		SubmitWithdrawalPathParams
	>,
	'path' | 'verb'
> &
	SubmitWithdrawalPathParams;

/**
 * Submit withdrawal form
 */
export const SubmitWithdrawal = ({
	product_name,
	account_number,
	...props
}: SubmitWithdrawalProps) => (
	<Mutate<
		WithdrawalResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		WithdrawalRequest,
		SubmitWithdrawalPathParams
	>
		verb="POST"
		path={`/accounts/${product_name}/${account_number}/withdrawals`}
		{...props}
	/>
);

export type UseSubmitWithdrawalProps = Omit<
	UseMutateProps<
		WithdrawalResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		WithdrawalRequest,
		SubmitWithdrawalPathParams
	>,
	'path' | 'verb'
> &
	SubmitWithdrawalPathParams;

/**
 * Submit withdrawal form
 */
export const useSubmitWithdrawal = ({
	product_name,
	account_number,
	...props
}: UseSubmitWithdrawalProps) =>
	useMutate<
		WithdrawalResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		WithdrawalRequest,
		SubmitWithdrawalPathParams
	>(
		'POST',
		(paramsInPath: SubmitWithdrawalPathParams) =>
			`/accounts/${paramsInPath.product_name}/${paramsInPath.account_number}/withdrawals`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface GetDirectDebitEligibilityQueryParams {
	/**
	 * Bank state branch number for the direct debit account.
	 */
	bsb?: string;
	/**
	 * Bank account number for the direct debit account.
	 */
	bank_account_number?: string;
}

export interface GetDirectDebitEligibilityPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetDirectDebitEligibilityProps = Omit<
	GetProps<
		GetDirectDebitEligibilityResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetDirectDebitEligibilityQueryParams,
		GetDirectDebitEligibilityPathParams
	>,
	'path'
> &
	GetDirectDebitEligibilityPathParams;

/**
 * Check direct debit eligibility
 */
export const GetDirectDebitEligibility = ({
	product_name,
	account_number,
	...props
}: GetDirectDebitEligibilityProps) => (
	<Get<
		GetDirectDebitEligibilityResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetDirectDebitEligibilityQueryParams,
		GetDirectDebitEligibilityPathParams
	>
		path={`/contributions/${product_name}/${account_number}/direct-debit-eligibility`}
		{...props}
	/>
);

export type UseGetDirectDebitEligibilityProps = Omit<
	UseGetProps<
		GetDirectDebitEligibilityResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetDirectDebitEligibilityQueryParams,
		GetDirectDebitEligibilityPathParams
	>,
	'path'
> &
	GetDirectDebitEligibilityPathParams;

/**
 * Check direct debit eligibility
 */
export const useGetDirectDebitEligibility = ({
	product_name,
	account_number,
	...props
}: UseGetDirectDebitEligibilityProps) =>
	useGet<
		GetDirectDebitEligibilityResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		GetDirectDebitEligibilityQueryParams,
		GetDirectDebitEligibilityPathParams
	>(
		(paramsInPath: GetDirectDebitEligibilityPathParams) =>
			`/contributions/${paramsInPath.product_name}/${paramsInPath.account_number}/direct-debit-eligibility`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface UpdateMemberAndAccountActionsPathParams {
	/**
	 * Unique ID assigned to the member at AustralianSuper.
	 */
	party_type_id: string;
	/**
	 * Source of action for the request.
	 */
	source: SourceTypes;
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type UpdateMemberAndAccountActionsProps = Omit<
	MutateProps<
		void,
		Error,
		void,
		ActionsUpdateRequest,
		UpdateMemberAndAccountActionsPathParams
	>,
	'path' | 'verb'
> &
	UpdateMemberAndAccountActionsPathParams;

/**
 * Update member and account actions
 *
 * Updates member actions and account actions for an account.
 */
export const UpdateMemberAndAccountActions = ({
	party_type_id,
	source,
	product_name,
	account_number,
	...props
}: UpdateMemberAndAccountActionsProps) => (
	<Mutate<
		void,
		Error,
		void,
		ActionsUpdateRequest,
		UpdateMemberAndAccountActionsPathParams
	>
		verb="PUT"
		path={`/actions/update/${party_type_id}/${source}/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseUpdateMemberAndAccountActionsProps = Omit<
	UseMutateProps<
		void,
		Error,
		void,
		ActionsUpdateRequest,
		UpdateMemberAndAccountActionsPathParams
	>,
	'path' | 'verb'
> &
	UpdateMemberAndAccountActionsPathParams;

/**
 * Update member and account actions
 *
 * Updates member actions and account actions for an account.
 */
export const useUpdateMemberAndAccountActions = ({
	party_type_id,
	source,
	product_name,
	account_number,
	...props
}: UseUpdateMemberAndAccountActionsProps) =>
	useMutate<
		void,
		Error,
		void,
		ActionsUpdateRequest,
		UpdateMemberAndAccountActionsPathParams
	>(
		'PUT',
		(paramsInPath: UpdateMemberAndAccountActionsPathParams) =>
			`/actions/update/${paramsInPath.party_type_id}/${paramsInPath.source}/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{
			pathParams: { party_type_id, source, product_name, account_number },
			...props,
		},
	);

export interface GetMemberActionsPathParams {
	/**
	 * Unique ID assigned to the member at AustralianSuper.
	 */
	party_type_id: string;
}

export type GetMemberActionsProps = Omit<
	GetProps<
		MemberActionsExpGetResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetMemberActionsPathParams
	>,
	'path'
> &
	GetMemberActionsPathParams;

/**
 * Retrieves Member Actions for a given party type ID.
 */
export const GetMemberActions = ({
	party_type_id,
	...props
}: GetMemberActionsProps) => (
	<Get<
		MemberActionsExpGetResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetMemberActionsPathParams
	>
		path={`/actions/${party_type_id}`}
		{...props}
	/>
);

export type UseGetMemberActionsProps = Omit<
	UseGetProps<
		MemberActionsExpGetResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetMemberActionsPathParams
	>,
	'path'
> &
	GetMemberActionsPathParams;

/**
 * Retrieves Member Actions for a given party type ID.
 */
export const useGetMemberActions = ({
	party_type_id,
	...props
}: UseGetMemberActionsProps) =>
	useGet<
		MemberActionsExpGetResponse,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| Response503
		| ErrorResponseResponse,
		void,
		GetMemberActionsPathParams
	>(
		(paramsInPath: GetMemberActionsPathParams) =>
			`/actions/${paramsInPath.party_type_id}`,
		{ pathParams: { party_type_id }, ...props },
	);

export interface GetBeneficiariesPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type GetBeneficiariesProps = Omit<
	GetProps<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetBeneficiariesPathParams
	>,
	'path'
> &
	GetBeneficiariesPathParams;

/**
 * Get all beneficiaries for the member account.
 *
 * Pass-through to beneficiary domain API.
 */
export const GetBeneficiaries = ({
	product_name,
	account_number,
	...props
}: GetBeneficiariesProps) => (
	<Get<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetBeneficiariesPathParams
	>
		path={`/beneficiaries/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseGetBeneficiariesProps = Omit<
	UseGetProps<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetBeneficiariesPathParams
	>,
	'path'
> &
	GetBeneficiariesPathParams;

/**
 * Get all beneficiaries for the member account.
 *
 * Pass-through to beneficiary domain API.
 */
export const useGetBeneficiaries = ({
	product_name,
	account_number,
	...props
}: UseGetBeneficiariesProps) =>
	useGet<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		GetBeneficiariesPathParams
	>(
		(paramsInPath: GetBeneficiariesPathParams) =>
			`/beneficiaries/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface UpdateBeneficiariesPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
	/**
	 * The member account ID in LINK
	 */
	account_number: string;
}

export type UpdateBeneficiariesProps = Omit<
	MutateProps<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		BeneficiariesUpdate,
		UpdateBeneficiariesPathParams
	>,
	'path' | 'verb'
> &
	UpdateBeneficiariesPathParams;

/**
 * Create new beneficiary group for the member account.
 *
 * Pass-through to beneficiary domain API.
 */
export const UpdateBeneficiaries = ({
	product_name,
	account_number,
	...props
}: UpdateBeneficiariesProps) => (
	<Mutate<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		BeneficiariesUpdate,
		UpdateBeneficiariesPathParams
	>
		verb="POST"
		path={`/beneficiaries/${product_name}/${account_number}`}
		{...props}
	/>
);

export type UseUpdateBeneficiariesProps = Omit<
	UseMutateProps<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		BeneficiariesUpdate,
		UpdateBeneficiariesPathParams
	>,
	'path' | 'verb'
> &
	UpdateBeneficiariesPathParams;

/**
 * Create new beneficiary group for the member account.
 *
 * Pass-through to beneficiary domain API.
 */
export const useUpdateBeneficiaries = ({
	product_name,
	account_number,
	...props
}: UseUpdateBeneficiariesProps) =>
	useMutate<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		BeneficiariesUpdate,
		UpdateBeneficiariesPathParams
	>(
		'POST',
		(paramsInPath: UpdateBeneficiariesPathParams) =>
			`/beneficiaries/${paramsInPath.product_name}/${paramsInPath.account_number}`,
		{ pathParams: { product_name, account_number }, ...props },
	);

export interface DeleteBeneficiariesPathParams {
	/**
	 * Product type. i.e Accumulation or Pension
	 */
	product_name: ProductTypes;
}

export type DeleteBeneficiariesProps = Omit<
	MutateProps<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		string,
		DeleteBeneficiariesPathParams
	>,
	'path' | 'verb'
> &
	DeleteBeneficiariesPathParams;

/**
 * Deletes the beneficiary group.
 *
 * Pass-through to beneficiary domain API.
 */
export const DeleteBeneficiaries = ({
	product_name,
	...props
}: DeleteBeneficiariesProps) => (
	<Mutate<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		string,
		DeleteBeneficiariesPathParams
	>
		verb="DELETE"
		path={`/beneficiaries/${product_name}`}
		{...props}
	/>
);

export type UseDeleteBeneficiariesProps = Omit<
	UseMutateProps<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		string,
		DeleteBeneficiariesPathParams
	>,
	'path' | 'verb'
> &
	DeleteBeneficiariesPathParams;

/**
 * Deletes the beneficiary group.
 *
 * Pass-through to beneficiary domain API.
 */
export const useDeleteBeneficiaries = ({
	product_name,
	...props
}: UseDeleteBeneficiariesProps) =>
	useMutate<
		BeneficiaryData,
		| Response400
		| Response401
		| Response403
		| Response404
		| Response500
		| ErrorResponseResponse,
		void,
		string,
		DeleteBeneficiariesPathParams
	>(
		'DELETE',
		(paramsInPath: DeleteBeneficiariesPathParams) =>
			`/beneficiaries/${paramsInPath.product_name}`,
		{ pathParams: { product_name }, ...props },
	);
